import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getProfile, updateProfileName } from "@/lib/documents.functions";
import { createBillingPortalSession, createCheckoutSession } from "@/lib/stripe.functions";
import { SiteShell } from "@/components/site-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({ meta: [{ title: "Settings — DealDraft" }] }),
  component: SettingsPage,
});

function SettingsPage() {
  const { user, session } = useAuth();
  const headers = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : undefined;
  const prof = useServerFn(getProfile);
  const save = useServerFn(updateProfileName);
  const checkout = useServerFn(createCheckoutSession);
  const portal = useServerFn(createBillingPortalSession);

  const q = useQuery({
    queryKey: ["profile"],
    queryFn: () => prof({ headers }),
    enabled: !!headers,
  });
  const [name, setName] = useState("");

  useEffect(() => {
    if (q.data?.full_name) setName(q.data.full_name);
  }, [q.data]);

  async function onSave() {
    try {
      await save({ data: { full_name: name }, headers });
      toast.success("Saved");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }
  async function onUpgrade() {
    try {
      const { url } = await checkout({ data: { origin: window.location.origin }, headers });
      window.location.href = url;
    } catch (e) {
      toast.error((e as Error).message);
    }
  }
  async function onPortal() {
    try {
      const { url } = await portal({ data: { origin: window.location.origin }, headers });
      window.location.href = url;
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const plan = q.data?.plan ?? "free";

  return (
    <SiteShell>
      <section className="mx-auto max-w-2xl px-6 py-10 space-y-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Profile</h2>
          <div className="mt-4 space-y-4">
            <div>
              <Label>Full name</Label>
              <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1.5" value={user?.email ?? ""} disabled />
            </div>
            <Button onClick={onSave}>Save</Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Billing</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Current plan: <span className="font-semibold capitalize text-foreground">{plan}</span>
            {plan === "pro" && " · Active"}
          </p>
          <div className="mt-4">
            {plan === "free" ? (
              <Button onClick={onUpgrade}>Upgrade to Pro — $19/month</Button>
            ) : (
              <Button variant="outline" onClick={onPortal}>
                Manage Subscription
              </Button>
            )}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
